if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);

        // GH2: keep the product visible while the drawer slides closed, then clear it
        clearTimeout(this.clearContentTimeout);
        this.clearContentTimeout = setTimeout(() => {
          if (this.hasAttribute('open')) return;
          this.modalContent.innerHTML = '';
          this.removeGallery();
        }, 600);
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = undefined;

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        clearTimeout(this.clearContentTimeout);
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        const galleryData = this.fetchGalleryData(opener);

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => galleryData.then((data) => [responseText, data]))
          .then(([responseText, data]) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            this.renderGallery(data);
            super.show(opener);
            this.subscribeToVariantChanges();
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      // GH2: full product gallery beside the drawer (desktop) or as a swipeable row (mobile)
      fetchGalleryData(opener) {
        try {
          const url = new URL(opener.getAttribute('data-product-url'), window.location.origin);
          const jsonUrl = `${url.pathname.replace(/\/$/, '')}.js`;
          const variantId = url.searchParams.get('variant');

          return fetch(jsonUrl)
            .then((response) => (response.ok ? response.json() : null))
            .then((product) => (product ? { product, variantId } : null))
            .catch(() => null);
        } catch (e) {
          return Promise.resolve(null);
        }
      }

      sizedImageUrl(src, width) {
        const url = new URL(src.startsWith('//') ? `https:${src}` : src, window.location.origin);
        url.searchParams.set('width', width);
        return url.toString();
      }

      renderGallery(data) {
        this.removeGallery();
        const product = data?.product;
        const media = (product?.media || []).filter((item) => item.src || item.preview_image?.src);
        if (!media.length) return;

        const gallery = document.createElement('div');
        gallery.className = 'quick-add-gallery';
        gallery.setAttribute('role', 'region');
        gallery.setAttribute('aria-label', product.title);

        const list = document.createElement('ul');
        list.className = 'quick-add-gallery__list list-unstyled';

        media.forEach((item, index) => {
          const image = item.media_type === 'image' ? item : item.preview_image || item;
          const src = image.src || item.src;
          const listItem = document.createElement('li');
          listItem.className = 'quick-add-gallery__item';
          listItem.dataset.mediaId = item.id;

          const img = document.createElement('img');
          img.src = this.sizedImageUrl(src, 1100);
          img.srcset = [550, 800, 1100, 1500].map((width) => `${this.sizedImageUrl(src, width)} ${width}w`).join(', ');
          img.sizes = '(min-width: 990px) 60rem, 85vw';
          img.alt = item.alt || product.title;
          img.loading = index < 2 ? 'eager' : 'lazy';
          if (image.width && image.height) {
            img.width = image.width;
            img.height = image.height;
          }

          listItem.appendChild(img);
          list.appendChild(listItem);
        });

        gallery.appendChild(list);
        this.querySelector('.quick-add-modal__content').prepend(gallery);
        this.galleryList = list;
        this.classList.add('quick-add-modal--gallery');

        const variants = product.variants || [];
        const variant =
          variants.find((item) => String(item.id) === String(data.variantId)) ||
          variants.find((item) => item.available) ||
          variants[0];
        this.scrollGalleryTo(variant?.featured_media?.id, false);
      }

      removeGallery() {
        this.querySelector('.quick-add-gallery')?.remove();
        this.galleryList = null;
        this.classList.remove('quick-add-modal--gallery');
      }

      scrollGalleryTo(mediaId, smooth = true) {
        if (!this.galleryList || !mediaId) return;
        const item = this.galleryList.querySelector(`[data-media-id="${mediaId}"]`);
        if (!item) return;

        const list = this.galleryList;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const behavior = smooth && !reducedMotion ? 'smooth' : 'auto';
        const horizontal = list.scrollWidth > list.clientWidth + 1;
        const styles = window.getComputedStyle(list);

        if (horizontal) {
          list.scrollTo({ left: item.offsetLeft - parseFloat(styles.paddingLeft || 0), behavior });
        } else {
          list.scrollTo({ top: item.offsetTop - parseFloat(styles.paddingTop || 0), behavior });
        }
      }

      subscribeToVariantChanges() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!this.hasAttribute('open')) return;
          this.scrollGalleryTo(event?.data?.variant?.featured_media?.id);
        });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
